import { IFile } from 'app/files/file.model';
import { transformStatus } from './status-transformer';
import { ServiceType } from 'app/core/service-type.model';

export interface TasksListResponse {
  anchor: Date;
  skip: number;
  take: number;
  count: number;
  tasks: TaskResponse[];
}

export enum Status {
  All = 'All',
  Canceled = 'Canceled',
  InProgress = 'InProgress',
  Submitted = 'Submitted',
  Done = 'Done',
  Failed = 'Failed',
  New = 'New',
  WaitForAction = 'WaitForAction'
}

export enum StatusIcon {
  InProgress = '/assets/icons/circle.svg',
  Done = '/assets/icons/circle.svg',
  Failed = '/assets/icons/canceled.svg',
  WaitForAction = '/assets/icons/circle.svg'
}

export enum StatusClass {
  InProgress = 'in-progress',
  Done = 'done',
  Failed = 'canceled',
  WaitForAction = 'exceptions'
}

interface TaskResponse {
  application: string;
  created: string;
  delivered: string;
  documents: TaskDocument[];
  email: string;
  id: string;
  isDeleted: boolean;
  isExtraError: boolean;
  number: number;
  progress: number;
  properties: any;
  started: string;
  status: string;
  type: string;
}

export interface OrderErrorsResponse {
  taskId: string;
  errors: OrderError[];
}

interface TaskDocument {
  files: TaskFiles;
  data: TaskDocumentData;
  isMoved: boolean;
}

interface TaskFiles {
  source: TaskFile;
  target: TaskFile;
}

interface TaskFile {
  id: string;
  token: string;
  name: string;
}

interface TaskDocumentData {
  currency: string;
  dueDate: Date;
  erpLink: string;
  invoiceDate: string;
  total: number;
  vendorId: number;
  vendorName: string;
}

export interface OrderFile extends IFile {
  name: string;
}

export class OrderError {
  date: Date;

  description: string;

  constructor(orderError) {
    this.date = new Date(orderError.date);
    this.description = orderError.description;
  }
}

export class OrderSourceFile implements OrderFile {
  erpLink: string;

  id: string;

  name: string;

  targets: OrderTargetFile[];

  token: string;

  dueDate: Date;

  invoiceDate: string;

  currency: string;

  total: number;

  vendor: string;

  constructor(document: TaskDocument) {
    const sourceFile = document.files.source;
    this.erpLink = document.data.erpLink;
    this.dueDate = document.data.dueDate && new Date(document.data.dueDate);
    this.invoiceDate = document.data.invoiceDate;
    this.id = sourceFile.id;
    this.name = sourceFile.name;
    this.targets = [];
    this.token = sourceFile.token;
    this.currency = document.data.currency || 'USD';
    this.total = Number(document.data.total);
    this.vendor = document.data.vendorName;
  }
}

interface TaskFileResponse {
  id: string;
  name: string;
  token: string;
}

export class OrderTargetFile implements OrderFile {
  id: string;
  name: string;
  token: string;

  constructor(data: TaskFileResponse) {
    this.id = data.id;
    this.name = data.name;
    this.token = data.token;
  }
}

export class Order {
  id: string;
  number: number;
  email: string;
  serviceType: ServiceType;
  application: string;

  created: Date;
  started: Date;
  deadline: Date;
  dueDate: Date;

  files: OrderSourceFile[];

  progress: number;
  service: string;
  status: string;
  isExtraError: boolean;

  constructor(task: TaskResponse) {
    const status = transformStatus(task.status);
    const files = task.documents && transformDocuments(task.documents);
    this.created = new Date(task.created);
    this.deadline = undefined;
    this.dueDate = files && files.length && minDueDate(files);
    this.email = task.email;
    this.id = task.id;
    this.serviceType = <ServiceType>task.type;
    this.application = task.application;
    this.files = files;
    this.number = task.number;
    this.progress = task.progress;
    this.service = task.type;
    this.started = task.started ? new Date(task.started) : void 0;
    this.status = status;
    this.isExtraError = task.isExtraError;
  }
}

export interface OrdersByStatus {
  [index: string]: Order[];
}

function minDueDate(files: OrderSourceFile[]): Date {
  let min = files[0].dueDate;
  for (const file of files) {
    if (min > file.dueDate) {
      min = file.dueDate;
    }
  }
  return min;
}

function transformDocuments(documents: TaskDocument[]): OrderSourceFile[] {
  const files: OrderSourceFile[] = [];
  const findName = 'target';
  for (const document of documents) {
    if (!document.files.source) {
      continue;
    }

    let sourceFile = files.find(file => file.id === document.files.source.id);
    if (!sourceFile) {
      sourceFile = new OrderSourceFile(document);
      files.push(sourceFile);
    }

    if (document.files.target) {
      const targetFullFileName = document.files.target.name;
      const targetFileName = targetFullFileName.substr(
        0,
        targetFullFileName.indexOf(targetFullFileName.split('.').pop()) - 1
      );
      let count = 1;
      for (const item in document.files) {
        if (item.indexOf(findName) > -1) {
          if (item !== findName) {
            const formatFile = document.files[item].name.split('.').pop();
            document.files[item].name =
              targetFileName + count + '.' + formatFile;
            count++;
          }
          sourceFile.targets.push(new OrderTargetFile(document.files[item]));
        }
      }
    }
  }
  (files || []).sort((a, b) => (b.name > a.name ? -1 : 1));

  return files;
}

export const openedOrders = {};
